import React, { FC } from 'react'
import { Tooltip } from 'antd'
import { GlobalOutlined } from '@ant-design/icons'
import { PLATFORM_CONFIG, PLATFORM_ICONS } from '../utils'

interface PlatformIconLinkProps {
  platform: string
  url: string
  /** Optional label rendered above the URL inside the tooltip (e.g. host name) */
  name?: string
  /** Icon size in px — default 20 */
  size?: number
}

/**
 * Renders a circular platform icon (Airbnb/VRBO/...) that, on hover, shows a
 * Tooltip containing a clickable link to the given URL. Falls back to a
 * branded-colour globe badge for unknown platforms.
 */
const PlatformIconLink: FC<PlatformIconLinkProps> = ({ platform, url, name, size = 20 }) => {
  const iconSrc = PLATFORM_ICONS[platform]
  const fallbackBg = PLATFORM_CONFIG[platform]?.color || '#1890FF'

  const tooltipContent = (
    <>
      {name && <div style={{ fontWeight: 500, marginBottom: 2 }}>{name}</div>}
      {url && (
        <a
          href={url}
          target='_blank'
          rel='noreferrer'
          onClick={e => e.stopPropagation()}
          style={{ color: '#fff', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {url}
        </a>
      )}
    </>
  )

  const iconEl = iconSrc ? (
    <img src={iconSrc} alt={platform} style={{ width: size, height: size }} />
  ) : (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: fallbackBg,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.55),
      }}
    >
      <GlobalOutlined />
    </span>
  )

  return (
    <Tooltip title={tooltipContent} mouseLeaveDelay={0.3}>
      {url ? (
        <a
          href={url}
          target='_blank'
          rel='noreferrer'
          onClick={e => e.stopPropagation()}
          style={{ display: 'inline-flex', lineHeight: 0, cursor: 'pointer' }}
        >
          {iconEl}
        </a>
      ) : (
        <span
          onClick={e => e.stopPropagation()}
          style={{ display: 'inline-flex', lineHeight: 0 }}
        >
          {iconEl}
        </span>
      )}
    </Tooltip>
  )
}

export default PlatformIconLink
